import React, { FC, useContext } from 'react';
import styled from 'styled-components';
import { BsCheckLg, BsPlusLg } from 'react-icons/bs';
import { MdEdit } from 'react-icons/md';
import {
  ManualProductsPicker,
  SlotData,
} from '@/features/editor/widgets/custom-widget/loading-section/manual-product-picker/context';
import { getSrc } from '@/components/catalog-search/products-grid';

interface P {
  active: boolean;
}
const Container = styled.div`
  position: relative;
  width: 177px;
  height: 188.5px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  background-color: #f3f6f7;
  border-radius: 21.9px;
  border: ${({ active }: P) => (active ? `2px solid blue` : `none`)};
`;

const SlotButton = styled.button`
  border: none;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  background: none;
  border-radius: 21.9px;

  &:hover {
    opacity: 0.8;
    cursor: pointer;
  }

  &:active {
    opacity: 1;
    cursor: default;
  }

  svg {
    min-height: 40px;
    margin-top: 1rem;
  }

  p {
    color: #5b656e;
    user-select: none;
    font-family: 'JetBrains Mono', serif;
    font-weight: 600;
    font-size: 1.5rem;
    margin: 10px 0 0 0;
    max-width: 150px;
    overflow-y: scroll;
    scrollbar-width: none;
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;

  img {
    max-width: 140px;
    max-height: 100px;
    border-radius: 10px;
    object-fit: cover;
  }
`;

const EditImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 15px;
  background: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;

  &:hover {
    opacity: 0.8;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &:active {
    opacity: 1;
    cursor: default;
  }
`;

export const Slot: FC<{ data: SlotData }> = ({ data }) => {
  const { setOpenSlot, selectImageForProduct } =
    useContext(ManualProductsPicker);

  const { product } = data;
  const pid = product?.id;
  const title = product?.title;
  const hasMultipleImages = product?.images && product.images.length > 1;

  const handleEditImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (product && hasMultipleImages) {
      selectImageForProduct(product, data.id);
    }
  };

  const renderSlotContent = () => {
    if (!pid) return <BsPlusLg size={40} color="#A6AFB8" />;
    if (product?.image?.src) {
      return (
        <ProductImageContainer>
          <img src={getSrc(product)} alt={title || `Product`} />
        </ProductImageContainer>
      );
    }
    return <BsCheckLg size={40} color="blue" />;
  };

  return (
    <Container active={!!title}>
      {hasMultipleImages && (
        <EditImageButton
          type="button"
          onClick={handleEditImage}
          title="Change product image"
        >
          <MdEdit size={18} color="#5B656E" />
        </EditImageButton>
      )}
      <SlotButton type="button" onClick={() => setOpenSlot(data.id)}>
        {renderSlotContent()}
        <p>
          {title ? (
            <span>
              {title}
              {` `}
              {product?.mainValue && (
                <span style={{ fontWeight: 800 }}>
                  <br />
                  {product?.mainValue}
                </span>
              )}
            </span>
          ) : (
            `slot ${data.id}`
          )}
        </p>
      </SlotButton>
    </Container>
  );
};
